import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import {wrapNotFound} from '../app/NotFoundHandler.react';
import PagingGrid, {defaultPageSize, processPagingParams} from '../fw/PagingGrid.react';
import AppPanel from '../fw/AppPanel.react';
import Link from 'react-router/lib/Link';

import types from '../../common/model-api/types';
import urlMapping from '../app/URLMapping';
import {injectIntl, defineMessages, FormattedMessage, FormattedDate, FormattedRelative} from 'react-intl';
import RefreshButton from '../jobs/RefreshButton.react';
import ConfirmDialog from '../fw/ConfirmDialog.react';
import DeleteRunningJobsMutation from './DeleteRunningJobsMutation.react';
import LoadingButton from '../fw/LoadingButton.react';
import moment from 'moment';
import { getFlowTitle } from '../flows/FlowConfig.react.js';
import { DEFAULT_DEDUPLICATION_DURATION_IN_MINUTES } from '../../common/model-api/FlowStepDefType.js';
import { RUNNIG_JOB_KEEP_ALIVE_FREQ } from '../../common/model-api/RunningJobType.js';

// Preload following screens
import(/* webpackChunkName: "RunningJobView" */ './RunningJobView.react.js');

const messages = defineMessages({
    title: {id: 'RunningJobsList2.title', 'defaultMessage': 'Running Task Locks'},
    deleteRunningJobsDescription: {id: 'RunningJobsList.deleteRunningJobsDescription2', 'defaultMessage': 'Do you really want to delete running job locks?'},
    deleteRunningJobsButton: {id: 'RunningJobsList.deleteRunningJobsButton', 'defaultMessage': 'Delete'},
});

class OrganisationRunningJobList extends React.Component {

    typeName = 'runningJobs';

    static propTypes = {
        intl: PropTypes.object.isRequired,
        relay: PropTypes.object,
        user: PropTypes.object.isRequired,
    };

    constructor(props) {
        super(props);

        this.state = {
            sortType: "expiresIn",
        };
    }

    onDeleteRunningJobs() {
		return new Promise((resolve, reject) => {
			let confirmDialogConfig  = {
				message: this.props.intl.formatMessage(messages.deleteRunningJobsDescription),
				confirm: this.props.intl.formatMessage(messages.deleteRunningJobsButton),
				confirmStyle: 'btn-danger'
			};

			return ConfirmDialog(this.props.intl, confirmDialogConfig).then(() => {
				return this.deleteRunningJobs().then(() => {
                    this.props.relay.forceFetch();

					return resolve();
				});
			}).catch((e) => {
                console.error(e);
				resolve();
			});
		});
	}

    deleteRunningJobs() {
		return new Promise((resolve, reject) => {
            try {
                this.props.relay.commitUpdate(
                    new DeleteRunningJobsMutation({
                        organisation: this.props.user.organisation,
                        beforeDate: moment().subtract(4, 'hours').toISOString()
                    }), {onSuccess: resolve, onFailure: reject}
                );
            } catch (e) {
                reject(e);
            }
		});
	}

    render() {
        const {runningJobs} = this.props.user.organisation;
        let sortedList = runningJobs.edges;
        let type = types[this.typeName];

        if (this.state.sortType === 'expiresIn') {
            sortedList = sortedList.sort((a, b) => {
                let value1 = a.node.expiresIn || moment().toISOString();
                let value2 = b.node.expiresIn || moment().toISOString();
                return moment(value1) - moment(value2);
            });
        } else if (this.state.sortType === 'createdAt') {
            sortedList = sortedList.sort((a, b) => {
                let value1 = a.node.createdAt || moment().toISOString();
                let value2 = b.node.createdAt || moment().toISOString();
                return moment(value1) - moment(value2);
            });
        }


        let list = {
            edges: sortedList,
            pageInfo: runningJobs.pageInfo
        };

        return (
            <AppPanel title={this.props.intl.formatMessage(messages.title)}>

                <div className="btn-toolbar" role="toolbar">
                    <RefreshButton autoRefresh relay={this.props.relay} />

                    <LoadingButton className={"btn btn-default"} onClick={() => { this.onDeleteRunningJobs(); }}>
                                <i aria-hidden="true" className="fa fa-fw fa-trash"></i> <FormattedMessage defaultMessage="Delete running job locks" id="RunningJobsList.deleteRunningJobs2" />
                    </LoadingButton>

                    <div aria-label="..." className="btn-group" role="group" style={{"float": "right"}}>
                            <LoadingButton className={"btn btn-xs btn-default " + (this.state.sortType === 'expiresIn' ? 'active' : '')} onClick={() => this.setState({'sortType': 'expiresIn'})}><FormattedMessage defaultMessage="Sort by expiration date" id="runningJobsList.expiresIn" /></LoadingButton>
                            <LoadingButton className={"btn btn-xs btn-default " + (this.state.sortType === 'createdAt' ? 'active' : '') } onClick={() => this.setState({'sortType': 'createdAt'})}><FormattedMessage defaultMessage="Sort by creation date" id="runningJobsList.createdAt" /></
                            LoadingButton>
                    </div>
                </div>

                <PagingGrid
                  columns={{
                      'organisation.name': {link: true},
                      'flow.name': {link: true,
                        render: (column, item, url, columnMapping) => {
                            return (
                                <Link to={urlMapping.getUrl(types["flows"], 'view', item.flow)}>
                                    {getFlowTitle(item.flow)}
                                </Link>
                            );
                        }
                      },
                      'flowStepDef.name': {link: true},
                      'flowInvocation': {
                        render: (column, item, url, columnMapping) => {
                            if (item.flowInvocation) {
                                return (
                                   <Link to={urlMapping.getUrl(types["flowInvocations"], 'view', item.flowInvocation)}>
                                      (<FormattedMessage defaultMessage="flow invocation" id="runningJobs.flowInvocation" />)
                                   </Link>
                                );
                            }
                        }
                      },
                      'expiresIn': {
                        render: (column, item, url, columnMapping) => {
                            let expiresInMinutes = Math.round(moment.duration(moment(item.expiresIn).diff(moment())).asMinutes());

                            return (
                                <span style={{"whiteSpace": "nowrap"}}>
                                    {expiresInMinutes >= 0 ? <FormattedMessage defaultMessage="in {deduplicationDuration} minutes" id="runningJobsList.expiresInMinutes" values={{deduplicationDuration: expiresInMinutes}} /> : <FormattedMessage defaultMessage="expired" id="runningJobsList.expired" values={{deduplicationDuration: expiresInMinutes}} />}<br/>
                                    <small><small><FormattedMessage defaultMessage="max {deduplicationDuration} minutes" id="runningJobsList.deduplicationDuration" values={{deduplicationDuration: item.flowStepDef.deduplicationDuration || DEFAULT_DEDUPLICATION_DURATION_IN_MINUTES}} /></small></small>
                                </span>
                            );
                        }
                      },
                      'createdAt': {
                        render: (column, item, url, columnMapping) => {
                            return (
                                <span style={{"whiteSpace": "nowrap"}}>
                                    <FormattedDate value={item.createdAt} /><br/>
                                    {item.keepAlive ? (
                                        moment(item.keepAlive).subtract(RUNNIG_JOB_KEEP_ALIVE_FREQ + 2, 'seconds').isBefore(moment()) ? <small><small style={{"color": "green"}}><FormattedMessage defaultMessage="Last mark is ok" id="runningJobsList.keepAliveIsOk" /></small></small> :
                                        <small><small><FormattedMessage defaultMessage="Last mark: " id="runningJobsList.lastMark" />{" "}<FormattedRelative value={item.keepAlive} /></small></small>)
                                    : null}
                                </span>
                            );
                        }
                      }
                  }}
                  edges={list}
                  getUrl={(row, column) => {
                      if (column === "flow.name") {
                          return urlMapping.getUrl(types["flows"], 'view', row.flow);
                      } else if (column === "organisation.name") {
                              return urlMapping.getUrl(types["organisations"], 'view', row.organisation);
                      }
                  }}
                  metadata={type}
                  pageInfo={runningJobs.pageInfo}
                  relay={this.props.relay}
                  renderLink
                  urlMapping={urlMapping}
                />

            </AppPanel>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(OrganisationRunningJobList), (props) => {
	return props.user.organisation;
}), {
    initialVariables: {
        first: defaultPageSize,
        after: null,
        before: null,
        last: null,
        organisationId: '',
    },
    prepareVariables (variables) {
        processPagingParams(variables);

        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                organisation(id: $organisationId) {
                    id,
                    realId,
                    runningJobs(first: $first, after: $after, before: $before, last: $last) {
                        edges {
                            node {
                                id,
                                realId,
                                createdAt,
                                expiresIn,
                                keepAlive,
                                flow {
                                    id,
                                    realId,
                                    organisation_id,
                                    name,
                                    selectedChannels,
                                    channels(first: 100) {
                                        edges {
                                            node {
                                                id,
                                                realId,
                                                name,
                                                channelDef {
                                                    id,
                                                    realId,
                                                    icon,
                                                    name,
                                                }
                                            }
                                        }
                                    },
                                    flowDef {
                                        id,
                                        realId,
                                        name,
                                    }
                                },
                                organisation {
                                    id,
                                    realId,
                                    name,
                                },
                                flowStepDef {
                                    id,
                                    realId,
                                    flowDef_id,
                                    deduplicationDuration,
                                    name,
                                },
                                flowInvocation {
                                    id,
                                    realId,
                                    organisation_id,
                                    flow_id,
                                },
                            }
                        },
                        pageInfo {
                            hasNextPage,
                            hasPreviousPage,
                            startCursor,
                            endCursor
                        }
                    }
                }
            }
        `,
    },
});
